import React, { useCallback, useEffect, useState } from 'react';

import { get } from '@rails/request.js';

import { getMetaContent, marketingUrl } from '../lib/meta';
import { cn } from '../lib/utils';

import { KEYCHAIN } from '../contexts/KeyMaster';
import { DeployChangelogContent, canShowDeployChangelog, deployChangelogPath, shouldAutoReloadForUpdate } from './DeployChangelogContent';
import { Modal } from './modals/Modal';
import { ModalBody } from './modals/ModalBody';
import { ModalContent } from './modals/ModalContent';
import { ModalPortal } from './modals/ModalPortal';
import { ModalTopBar } from './modals/ModalTopBar';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_TITLE_CLASS } from './shared/settingsModalChrome';

const NAG_BAR_REASONS = new Set(['restart', 'unsupported']);
const INITIAL_CHANGELOG_STATE = { changelog: null, status: 'idle' };

const NagBar = () => {
  const [reason, setReason] = useState(null);
  const [deployBuilds, setDeployBuilds] = useState({ previousBuild: null, build: null });
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [changelogState, setChangelogState] = useState(INITIAL_CHANGELOG_STATE);

  const open = reason === 'restart' || reason === 'unsupported';
  const isStaff = getMetaContent('current_user:is_staff') === 'true';
  const showDeployChangelog = canShowDeployChangelog({ isStaff, ...deployBuilds });

  useEffect(() => {
    const handler = (event) => {
      const r = event.detail?.reason;
      if (!NAG_BAR_REASONS.has(r)) {
        throw new Error(`output:internal:nagbar: expected detail.reason to be one of ${[...NAG_BAR_REASONS].join(', ')}, got ${String(r)}`);
      }
      setReason(r);
      if (r === 'restart') {
        setDeployBuilds({ previousBuild: event.detail?.previousBuild || null, build: event.detail?.build || null });
      }
    };
    window.addEventListener('output:internal:nagbar', handler);
    return () => window.removeEventListener('output:internal:nagbar', handler);
  }, []);

  // when restart nag bar is shown, auto-reload when app enters background
  useEffect(() => {
    if (reason !== 'restart') return;
    const handler = () => {
      if (shouldAutoReloadForUpdate({ reason, isChangelogOpen, visibilityState: document.visibilityState })) {
        window.dispatchEvent(new CustomEvent('output:reload', { detail: { source: 'nagbar', reason: 'restart-auto-on-hidden' } }));
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isChangelogOpen, reason]);

  const handleReload = useCallback(() => {
    window.dispatchEvent(new CustomEvent('output:reload', { detail: { source: 'nagbar', reason: 'restart-apply-button' } }));
  }, []);

  const handleCloseChangelog = useCallback(() => setIsChangelogOpen(false), []);

  const handleOpenChangelog = useCallback(async () => {
    setIsChangelogOpen(true);
    setChangelogState({ changelog: null, status: 'loading' });

    try {
      const response = await get(deployChangelogPath(deployBuilds), { responseKind: 'json' });
      const payload = await response.json;
      if (!(response.ok && payload?.ok)) throw new Error('unavailable');
      setChangelogState({ changelog: payload.data.changelog, status: 'loaded' });
    } catch {
      setChangelogState({ changelog: null, status: 'error' });
    }
  }, [deployBuilds]);

  let body = null;
  if (reason === 'restart') {
    body = (
      <div className="flex min-h-8 shrink-0 items-center justify-center gap-3 border-b border-gray-200 px-4 pt-1 text-xs text-black dark:border-neutral-700 dark:text-white">
        <span className="text-center font-medium">A new version of the app is available.</span>
        {showDeployChangelog ? (
          <button type="button" onClick={handleOpenChangelog} className="shrink-0 cursor-pointer font-medium underline underline-offset-2">
            What&apos;s new
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleReload}
          className="shrink-0 cursor-pointer rounded-md bg-black px-2 py-1 text-xs font-medium text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-neutral-100"
        >
          Apply Update
        </button>
      </div>
    );
  } else if (reason === 'unsupported') {
    body = (
      <div className="flex min-h-8 shrink-0 items-center justify-center gap-3 border-b border-gray-200 px-4 pt-1 text-xs text-black dark:border-neutral-700 dark:text-white">
        <span className="text-center font-medium">This version of the app is no longer supported. Please download the latest version.</span>
        <a
          href={marketingUrl('/apps')}
          target="_blank"
          className="shrink-0 cursor-pointer rounded-md bg-black px-2 py-1 text-xs font-medium text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-neutral-100"
        >
          Download Latest
        </a>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'flex flex-none flex-col overflow-hidden transition-[max-height] duration-200 ease-out',
          open ? 'max-h-14' : 'max-h-0',
        )}
        data-app-region="none"
      >
        {body}
      </div>
      <ModalPortal
        open={isChangelogOpen}
        onClose={handleCloseChangelog}
        keychainId={KEYCHAIN.MODAL__DEPLOY_CHANGELOG}
        size="settings"
        className="md:!h-160 md:!max-h-[calc(100dvh-2rem)] md:!max-w-128"
      >
        <Modal
          onClose={handleCloseChangelog}
          className="rounded-2xl"
          closePlacement="top-left"
          showClose={{ desktop: true, mobile: true }}
          padding="none"
          closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
          closeIcon={SETTINGS_MODAL_CLOSE_ICON}
        >
          <ModalBody padding="none" className="h-full overflow-hidden">
            <div className="shrink-0 pt-5">
              <ModalTopBar className="!pb-0">
                <span className={SETTINGS_MODAL_TITLE_CLASS}>What&apos;s new</span>
              </ModalTopBar>
            </div>
            <ModalContent className="min-h-0 px-4 pb-5 md:px-5">
              <DeployChangelogContent state={changelogState} {...deployBuilds} />
            </ModalContent>
          </ModalBody>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default NagBar;
